import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import api from '../services/api';

export interface PlatformSettings {
  membershipFee: number;
  standardDeliveryCharge: number;
  referrerReward: number;
  referredUserReward: number;
  isReferralEnabled: boolean;
  isInviteCodeRequired: boolean;
}

interface SettingsContextValue {
  settings: PlatformSettings | null;
  refreshSettings: () => Promise<void>;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: null,
  refreshSettings: async () => {},
});

const STORAGE_KEY = 'ani_settings_version';

export const SettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<PlatformSettings | null>(null);

  const refreshSettings = useCallback(async () => {
    try {
      const response = await api.get('/platform/settings');
      const data = response.data;
      const next: PlatformSettings = {
        membershipFee: data.membershipFee ?? 199,
        standardDeliveryCharge: data.standardDeliveryCharge ?? 50,
        referrerReward: data.referrerReward ?? 50,
        referredUserReward: data.referredUserReward ?? 20,
        isReferralEnabled: data.isReferralEnabled ?? true,
        isInviteCodeRequired: data.isInviteCodeRequired ?? true,
      };
      setSettings(next);
      // Broadcast the change to other open tabs so every tab refreshes instantly.
      localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // Silent — pages fall back to their defaults while offline.
    }
  }, []);

  // Load once on mount, then keep listening for admin saves in other tabs.
  useEffect(() => {
    refreshSettings();
  }, [refreshSettings]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        refreshSettings();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [refreshSettings]);

  return (
    <SettingsContext.Provider value={{ settings, refreshSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => useContext(SettingsContext);
